import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";

const RegistrationCardPreview = ({
  showModal,
  onClose,
  pdfUrl,
  htmlContent,
  guestDetails,
  type = "registration-card",
  outputType,
  handleSwitchView,
  handleEmail,
  loading,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  // Determine the document name based on type
  const documentName = type === "confirmation-card" ? "Reservation Confirmation" : "Reservation Card";

  useEffect(() => {
    if (showModal && !pdfUrl && !htmlContent) {
      setIsLoading(true);
      Swal.fire({
        title: `Loading Guest ${documentName}...`,
        text: `Please wait while the ${documentName.toLowerCase()} is being prepared.`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    }
  }, [showModal, documentName]);

  useEffect(() => {
    if (showModal && (pdfUrl || htmlContent)) {
      setIsLoading(false);
      setIsSwitching(false);
      Swal.close();
    }
  }, [showModal, pdfUrl, htmlContent]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    } else {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
      Swal.close();
    };
  }, [showModal]);

  const handleClose = () => {
    onClose();
    setIsSwitching(false);
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${documentName.replace(/\s+/g, "_")}_${guestDetails?.booking_id || "unknown"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (htmlContent) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Guest ${documentName}</title>
            <style>
              @page { size: A4; margin: 5mm; }
              body { margin: 0; padding: 0; }
              .a4-container {
                width: 210mm;
                min-height: 297mm;
                margin: 0 auto;
                box-sizing: border-box;
              }
            </style>
          </head>
          <body>
            <div class="a4-container">
              ${htmlContent}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleViewSwitch = (newOutputType) => {
    setIsSwitching(true);
    handleSwitchView(newOutputType);
  };

  const handleEmailCard = async () => {
    try {
      setIsSwitching(true);

      // Validate guestDetails.email
      const email = guestDetails?.email?.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email format validation

      if (!email || email.toLowerCase() === "na" || email.toLowerCase() === "n/a" || !emailRegex.test(email)) {
        throw new Error("Guest is not associated with a valid email address.");
      }

      // Show confirmation dialog
      const confirmation = await Swal.fire({
        title: `Send Guest ${documentName}`,
        text: `Are you sure you want to send the ${documentName.toLowerCase()} to ${email}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Send",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
      });

      if (!confirmation.isConfirmed) {
        return; // Exit if user cancels
      }

      // Proceed with sending the email
      const result = await handleEmail();

      // Show success message
      await Swal.fire({
        title: "Email Sent",
        text: `${documentName} emailed successfully to ${email}!`,
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: error.message || `Failed to email the Guest ${documentName}.`,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const title = `Guest ${documentName} Preview`;

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      keyboard={false}
      dialogClassName="h-100 m-0 position-fixed end-0 invoice-preview-modal-dialog"
      backdropClassName="modal-backdrop"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderBottom: "1px solid #dee2e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Modal.Title id="pdfPreviewModalLabel">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          flex: 1,
          padding: 0,
          overflowY: "auto",
          position: "relative",
        }}
      >
        {(loading || isLoading || isSwitching) ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : htmlContent ? (
          <div
            style={{
              overflowY: "auto",
              padding: "5mm",
              display: "flex",
              justifyContent: "center",
              minHeight: "100%",
            }}
          >
            <div
              style={{
                width: "210mm",
                height: "auto",
                padding: "10px",
                boxSizing: "border-box",
                backgroundColor: "#fff",
                boxShadow: "0 0 5px rgba(0,0,0,0.1)",
              }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        ) : pdfUrl ? (
          <div
            style={{
              overflowY: "auto",
              padding: "5mm",
              display: "flex",
              justifyContent: "center",
              minHeight: "100%",
            }}
          >
            <iframe
              src={pdfUrl}
              style={{
                width: "210mm",
                height: "auto",
                border: "none",
              }}
              title={title}
              loading="lazy"
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            No content to display
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderTop: "1px solid #dee2e6",
          backgroundColor: "#fff",
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          display: "flex",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <Button
          variant={outputType === "pdf" ? "outline-primary" : "primary"}
          onClick={() => handleViewSwitch(outputType === "pdf" ? "html" : "pdf")}
          style={{ fontSize: "14px", padding: "5px 10px" }}
          disabled={loading || isLoading || isSwitching}
        >
          Switch to {outputType === "pdf" ? "HTML" : "PDF"} View
        </Button>
        <Button
          variant="primary"
          onClick={handleEmailCard}
          style={{ fontSize: "14px", padding: "5px 10px" }}
          disabled={loading || isLoading || isSwitching}
        >
          Email {documentName.split(" ").pop()}
        </Button>
        {outputType === "pdf" ? (
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={!pdfUrl || loading || isLoading || isSwitching}
            style={{ fontSize: "14px", padding: "5px 10px" }}
          >
            Download PDF
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={!htmlContent || loading || isLoading || isSwitching}
            style={{ fontSize: "14px", padding: "5px 10px" }}
          >
            Print
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{ fontSize: "14px", padding: "5px 10px" }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Add custom styles for the modal dialog
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
  .invoice-preview-modal-dialog {
    width: 230mm;
    max-width: 100%;
    position: fixed;
    right: 0;
    height: 100%;
  }
`;
document.head.appendChild(styleSheet);

export default RegistrationCardPreview;